// ========================================
// Comunidad & Chat View — the school's internal channels
// ========================================

const ComunidadView = {
    _canales: [],
    _selectedCanalId: 'canal_general',
    _mensajes: [],
    _refreshInterval: null,
    _lastMessageCount: 0,
    _chatBlocked: false,
    _active: false,
    _viewEpoch: 0,
    _channelRequest: 0,
    _messageRequest: 0,
    _allowedTargetFormations: [],
    _loadError: '',
    _sending: false,
    _readThrough: new Map(),
    _readPending: new Set(),
    _unreadBoundary: null,
    _messagesLoaded: false,
    // Older messages remain on the server («Ver mensajes anteriores»): channels load in pages.
    _hasOlder: false,
    _loadingOlder: false,

    render() {
        return `
        <div id="comunidad-layout" class="com-layout cmx-layout">
            <!-- Left Panel: Canales -->
            <aside class="com-channels cmx-channels">
                <div class="cmx-channels-head">
                    <div class="cmx-channels-title">
                        <h1>${Utils.sanitizeHTML(SchoolService.label('Comunidad'))}</h1>
                        <span>Foros y proyectos de cine</span>
                    </div>
                    <button type="button" class="cmx-new" ${UIEvents.attr("click", function(event,captured){ComunidadView.openCreateChannelModal();}, [])} title="Crear canal o proyecto"><i class="fas fa-plus" aria-hidden="true"></i> Canal</button>
                </div>
                <div class="cmx-channel-search">
                    <input type="search" id="comunidad-channel-search" placeholder="Buscar canal" aria-label="Buscar canal" value="${Utils.sanitizeHTML(this._channelQuery || '')}"
                        ${UIEvents.attr("input", function(event,captured){ComunidadView.setChannelQuery(this.value);}, [])}>
                </div>
                <div id="comunidad-channels-list" class="cmx-channel-list">
                    <p class="cmx-muted cmx-center"><i class="fas fa-spinner fa-spin" aria-hidden="true"></i> Cargando canales…</p>
                </div>
            </aside>

            <!-- Right Panel: Chat Area -->
            <section class="com-chat cmx-chat">
                <div id="comunidad-active-header" class="cmx-chat-head">
                    <!-- Mobile only: back from the chat to the channel list. -->
                    <button type="button" class="com-back" aria-label="Volver a los canales" ${UIEvents.attr("click", function(event,captured){ComunidadView.showChannelList();}, [])}><i class="fas fa-arrow-left"></i></button>
                    <div class="cmx-chat-title">
                        <h2 id="active-channel-title">Cargando…</h2>
                        <p id="active-channel-desc"></p>
                    </div>
                </div>

                <div id="comunidad-chat-messages" class="cmx-messages"></div>

                <div class="cmx-composer">
                    <textarea id="comunidad-msg-input" rows="2" maxlength="5000" aria-label="Mensaje para el canal" ${UIEvents.attr("keydown", function(event,captured){ComunidadView.handleInputKeydown(event);}, [])} placeholder="Escribe un mensaje…"></textarea>
                    <button type="button" class="cmx-send" ${UIEvents.attr("click", function(event,captured){ComunidadView.sendMessage();}, [])}>
                        <span>Enviar</span> <i class="fas fa-paper-plane" aria-hidden="true"></i>
                    </button>
                </div>
            </section>
        </div>`;
    },

    updateInputState() {
        const input = document.getElementById('comunidad-msg-input');
        if (!input) return;
        const button = input.nextElementSibling;
        const off = this._chatBlocked || !this._selectedCanalId || this._loadError || this._sending;
        const active = this._canales.find(c => c.id === this._selectedCanalId);
        input.disabled = !!off;
        input.placeholder = this._chatBlocked ? 'Tu participación está bloqueada. Contacta con administración.' : this._sending ? 'Enviando mensaje…'
            : off ? 'Selecciona un canal disponible o reintenta la carga.' : `Escribe en ${active ? active.name : 'el canal'}… (Intro envía, Mayús+Intro salta de línea)`;
        if (button) button.disabled = !!off;
    },

    async init() {
        this.detachReadListeners();
        this._active = true;
        ++this._viewEpoch;
        this._readThrough = new Map();
        this._readPending = new Set();
        this._onReadVisible = () => { this.markRenderedMessages(); };
        this._readContainer = document.getElementById('comunidad-chat-messages');
        this._readContainer?.addEventListener('scroll', this._onReadVisible);
        document.addEventListener('visibilitychange', this._onReadVisible);
        window.addEventListener('focus', this._onReadVisible);
        this._canales = [];
        this._lastEstado = null;
        this.clearMessages();
        await this.loadCanales();
        this.startRefreshTimer();
        this.updateInputState();
    },

    destroy() {
        this.detachReadListeners();
        this._active = false;
        ++this._viewEpoch;
        this._canales = [];
        this._mensajes = [];
        this._unreadBoundary = null;
        this._messagesLoaded = false;
        this._allowedTargetFormations = [];
        if (this._refreshInterval) {
            clearInterval(this._refreshInterval);
            this._refreshInterval = null;
        }
    },

    detachReadListeners() {
        if (!this._onReadVisible) return;
        this._readContainer?.removeEventListener('scroll', this._onReadVisible);
        document.removeEventListener('visibilitychange', this._onReadVisible);
        window.removeEventListener('focus', this._onReadVisible);
        this._onReadVisible = null;
        this._readContainer = null;
    },

    isAtBottom() {
        const pane = document.getElementById('comunidad-chat-messages');
        return !!pane && !this.chatHidden() && pane.scrollHeight - pane.scrollTop - pane.clientHeight <= 24;
    },

    // Phone showing the channel list (comunidad.css): the chat is off screen, so nothing in it
    // counts as read until the person opens the channel.
    chatHidden() {
        const layout = document.getElementById('comunidad-layout');
        return !!layout && !layout.classList.contains('com-show-chat') && !!window.matchMedia && window.matchMedia('(max-width: 992px)').matches;
    },

    async markRenderedMessages() {
        if (!this._active || this._loadError || document.visibilityState !== 'visible' || !document.hasFocus() || !this.isAtBottom()) return;
        const channel = this._selectedCanalId, message = this._mensajes[this._mensajes.length - 1];
        if (!channel || !message || this._readThrough.get(channel) === message.id) return;
        const key = channel + ':' + message.id, epoch = this._viewEpoch, token = AuthManager.getCsrfToken();
        if (this._readPending.has(key)) return;
        const pending = this._readPending;
        pending.add(key);
        const current = () => this._active && epoch === this._viewEpoch && token === AuthManager.getCsrfToken();
        try {
            const response = await fetch('/api/comunidad/canales/' + encodeURIComponent(channel) + '/leidos', {
                method:'POST', headers:{'Content-Type':'application/json','X-SICAF-CSRF':token}, body:JSON.stringify({messageId:message.id})
            });
            const result = await response.json();
            if (!current() || !response.ok || !result.success) return;
            this._readThrough.set(channel, message.id);
            const row = this._canales.find(c => c.id === channel);
            if (row && Number.isSafeInteger(result.unreadCount) && result.unreadCount >= 0) {
                // Discard any channel-list request made before this acknowledgment.
                ++this._channelRequest;
                row.unreadCount = result.unreadCount;
                this.renderChannelsList();
            }
            if (typeof CommunityUnread !== 'undefined') await CommunityUnread.refresh();
        } catch { /* Keep the server count; retry after the next visible refresh. */ }
        finally { pending.delete(key); }
    },

    startRefreshTimer() {
        if (!this._active) return;
        if (this._refreshInterval) clearInterval(this._refreshInterval);
        // Poll every 3 seconds to keep chat active, sync messages, channels and notifications
        this._refreshInterval = setInterval(async () => {
            if (this._refreshing || !this._active) return;
            this._refreshing = true;
            try {
                // Reload channels/messages only when the server fingerprint moved
                // (new message, read cursor, access change) or the last load failed.
                const estado = typeof CommunityUnread !== 'undefined' ? await CommunityUnread.fetchEstado() : null;
                const key = estado && `${estado}|${this._selectedCanalId}`;
                if (!key || key !== this._lastEstado || this._loadError) {
                    await this.loadCanalesSilent();
                    if (this._selectedCanalId && this._active) await this.refreshMessagesSilent();
                    this._lastEstado = this._loadError ? null : key;
                }

                // Sincronizar notificaciones de la campana en paralelo.
                if (typeof FileStorage !== 'undefined' && FileStorage.syncCommunicationsIfChanged) {
                    FileStorage.syncCommunicationsIfChanged({ messages: false });
                }
            } finally { this._refreshing = false; }
        }, 3000);
    },

    async loadCanalesSilent() {
        return this.loadCanales(false);
    },

    clearMessages(error = '') {
        this._mensajes = [];
        this._hasOlder = false;
        this._unreadBoundary = null;
        this._messagesLoaded = false;
        this._lastMessageCount = 0;
        this._loadError = error;
        this.renderMessagesList();
        this.updateInputState();
    },

    async loadCanales(loadMessages = true) {
        const epoch = this._viewEpoch, request = ++this._channelRequest;
        const token = AuthManager.getCsrfToken();
        const current = () => this._active && epoch === this._viewEpoch && request === this._channelRequest && token === AuthManager.getCsrfToken();
        try {
            const res = await fetch('/api/comunidad/canales', { headers: { 'X-SICAF-CSRF': token } });
            const data = await res.json();
            if (!current()) return;
            if (!res.ok || !data.success) throw new Error('No se pudieron verificar los canales. Reintenta o inicia sesión de nuevo.');
            this._chatBlocked = !!data.chatBlocked;
            this._allowedTargetFormations = data.allowedTargetFormations;
            this._canales = data.canales || [];
            const previous = this._selectedCanalId;
            if (!this._canales.some(c => c.id === previous)) this._selectedCanalId = this._canales[0]?.id || null;
            if (previous !== this._selectedCanalId) this.clearMessages();
            this.renderChannelsList();
            this.updateActiveChannelHeader();
            this.updateInputState();
            if (loadMessages || previous !== this._selectedCanalId) await this.loadMessages(true);
        } catch {
            if (!current()) return;
            this._canales = [];
            this._selectedCanalId = null;
            this._allowedTargetFormations = [];
            ++this._messageRequest;
            this.renderChannelsList();
            this.updateActiveChannelHeader();
            this.clearMessages('No se pudieron verificar los canales. Reintenta o inicia sesión de nuevo.');
        }
    },

    _channelQuery: '',

    setChannelQuery(value) { this._channelQuery = String(value || ''); this.renderChannelsList(); },

    // Who can read a channel: the formations it targets, or the whole school.
    _accessText(c) {
        const ids = Array.isArray(c.targetFormations) ? c.targetFormations : [];
        if (!ids.length) return 'Toda la escuela';
        const formations = typeof DataManager !== 'undefined' && DataManager.getFormations ? DataManager.getFormations() : [];
        const names = ids.map(id => formations.find(f => String(f.id) === String(id))?.name).filter(Boolean);
        if (!names.length) return `${ids.length} ${ids.length === 1 ? 'curso' : 'cursos'}`;
        return names.length > 2 ? `${names.slice(0, 2).join(' · ')} +${names.length - 2}` : names.join(' · ');
    },

    renderChannelsList() {
        const list = document.getElementById('comunidad-channels-list');
        if (!list) return;

        if (this._canales.length === 0) {
            SafeHTML.set(list, '<p class="cmx-muted cmx-center">No hay canales disponibles</p>');
            return;
        }

        const user = AuthManager.getCurrentUser();
        const isAdmin = user && user.role === 'admin';
        const q = typeof Utils !== 'undefined' && Utils.normalizeString ? Utils.normalizeString(this._channelQuery || '').trim() : String(this._channelQuery || '').toLowerCase().trim();
        const norm = v => typeof Utils !== 'undefined' && Utils.normalizeString ? Utils.normalizeString(String(v || '')) : String(v || '').toLowerCase();
        const shown = this._canales.filter(c => !q || norm(`${c.name} ${c.description || ''}`).includes(q));

        const item = c => {
            const isActive = c.id === this._selectedCanalId;
            const unreadCount = Number.isSafeInteger(c.unreadCount) && c.unreadCount > 0 ? c.unreadCount : 0;
            const unreadBadge = unreadCount ? `<span class="community-channel-unread cmx-unread" aria-label="${unreadCount} ${unreadCount === 1 ? 'mensaje sin leer' : 'mensajes sin leer'}">${unreadCount > 99 ? '99+' : unreadCount}</span>` : '';
            // Admins can delete every channel (default ones too); creators their own.
            const isCreator = user && String(c.creatorId) === String(user.id);
            const showDelete = isAdmin || (isCreator && !c.isDefault);
            const deleteButtonHtml = showDelete ? `<button type="button" class="cmx-channel-delete" aria-label="Eliminar el canal ${Utils.sanitizeHTML(c.name)}" title="Eliminar canal"
                ${UIEvents.attr("click", function(event,captured){ComunidadView.deleteChannel(event,String(captured[0]));}, [c.id])}><i class="fas fa-trash-alt" aria-hidden="true"></i></button>` : '';
            return `
            <div class="cmx-channel ${isActive ? 'is-active' : ''} ${unreadCount ? 'is-unread' : ''}" role="button" tabindex="0" aria-current="${isActive ? 'page' : 'false'}"
                ${UIEvents.attr("click", function(event,captured){ComunidadView.openChannel(String(captured[0]));}, [c.id])}
                ${UIEvents.attr("keydown", function(event,captured){if(event.key==='Enter'||event.key===' '){event.preventDefault();ComunidadView.openChannel(String(captured[0]));}}, [c.id])}>
                <span class="cmx-channel-icon ${c.isDefault ? '' : 'is-project'}" aria-hidden="true">${c.isDefault ? '#' : '<i class="fas fa-bullhorn"></i>'}</span>
                <span class="cmx-channel-main">
                    <b>${Utils.sanitizeHTML(c.name)}</b>
                    <small>${Utils.sanitizeHTML(c.description || this._accessText(c))}</small>
                </span>
                ${unreadBadge}
                ${deleteButtonHtml}
            </div>`;
        };
        const general = shown.filter(c => c.isDefault), projects = shown.filter(c => !c.isDefault);
        SafeHTML.set(list, (shown.length ? '' : '<p class="cmx-muted cmx-center">Ningún canal coincide.</p>')
            + (general.length ? `<span class="cmx-label">Generales</span>${general.map(item).join('')}` : '')
            + (projects.length ? `<span class="cmx-label">Proyectos y castings</span>${projects.map(item).join('')}` : ''));
    },

    updateActiveChannelHeader() {
        const titleEl = document.getElementById('active-channel-title');
        const descEl = document.getElementById('active-channel-desc');
        if (!titleEl || !descEl) return;

        const active = this._canales.find(c => c.id === this._selectedCanalId);
        if (active) {
            titleEl.textContent = active.name;
            descEl.textContent = [`Quién lo ve: ${this._accessText(active)}${(active.targetFormations || []).length ? ' y el personal' : ''}`,
                active.description, active.creatorName ? `creado por ${active.creatorName}` : ''].filter(Boolean).join(' · ');
        } else {
            titleEl.textContent = 'Selecciona un canal disponible';
            descEl.textContent = '';
        }
        this.updateInputState();
    },

    // On a phone the list and the chat take turns on screen (see comunidad.css): opening a
    // channel shows the chat; the back arrow returns to the list.
    openChannel(canalId) {
        document.getElementById('comunidad-layout')?.classList.add('com-show-chat');
        if (canalId !== this._selectedCanalId || !this._messagesLoaded || this._loadError) return this.selectChannel(canalId);
        // Already loaded while hidden: place it now that it is visible, then mark what is read.
        if (this._unreadBoundary) this.scrollToUnread(); else this.scrollToBottom();
        return this.markRenderedMessages();
    },

    showChannelList() {
        document.getElementById('comunidad-layout')?.classList.remove('com-show-chat');
    },

    async selectChannel(canalId) {
        if (!this._canales.some(c => c.id === canalId)) return;
        if (canalId === this._selectedCanalId && this._messagesLoaded && !this._loadError) return;
        this._selectedCanalId = canalId;
        this.clearMessages();
        this.renderChannelsList();
        this.updateActiveChannelHeader();
        await this.loadMessages(true);
    },

    async loadMessages(shouldScroll = true) {
        const channel = this._selectedCanalId, epoch = this._viewEpoch, request = ++this._messageRequest;
        if (!channel) { this.clearMessages(); return; }
        const token = AuthManager.getCsrfToken();
        const current = () => this._active && epoch === this._viewEpoch && channel === this._selectedCanalId && request === this._messageRequest && token === AuthManager.getCsrfToken();
        // Once a channel is open, a refresh asks from the oldest message shown: what the reader
        // already has plus anything new (and without the deleted ones), never the whole history.
        const oldest = this._messagesLoaded ? this._mensajes[0]?.cursor : undefined;
        const query = Number.isSafeInteger(oldest) ? `?from=${oldest}` : '';
        try {
            const res = await fetch('/api/comunidad/canales/' + encodeURIComponent(channel) + '/mensajes' + query, { headers: { 'X-SICAF-CSRF': token } });
            const data = await res.json();
            if (!current()) return;
            if (!res.ok || !data.success) throw new Error('Canal no disponible');
            const incoming = data.mensajes || [];
            const olderChanged = this._hasOlder !== !!data.hasMore;
            this._hasOlder = !!data.hasMore;
            const wasAtBottom = this.isAtBottom();
            const firstLoad = !this._messagesLoaded;
            const pane = document.getElementById('comunidad-chat-messages');
            const previousScroll = pane?.scrollTop || 0;
            const canFollow = wasAtBottom && document.visibilityState === 'visible' && document.hasFocus();
            // Freeze the entry boundary for this visit, even after the read receipt
            // updates. Polling must not move the divider while the user is reading.
            if (!this._unreadBoundary && (firstLoad || !canFollow) && Number.isSafeInteger(data.unreadCount) && data.unreadCount > 0) {
                const firstUnread = incoming.find(m => m.id === data.firstUnreadId);
                if (firstUnread) this._unreadBoundary = {id:firstUnread.id,sequence:firstUnread.sequence,count:data.unreadCount};
            }
            const changed = olderChanged || JSON.stringify(incoming) !== JSON.stringify(this._mensajes) || this._loadError;
            this._loadError = '';
            this._mensajes = incoming;
            this._lastMessageCount = incoming.length;
            this._messagesLoaded = true;
            if (changed) this.renderMessagesList();
            this.updateInputState();
            if (firstLoad && this._unreadBoundary) this.scrollToUnread();
            else if ((shouldScroll || canFollow) && changed) this.scrollToBottom();
            else if (changed && pane) pane.scrollTop = previousScroll;
            await this.markRenderedMessages();
        } catch {
            if (current()) this.clearMessages('No se pueden mostrar los mensajes. Puede haber cambiado tu acceso o fallado la conexión.');
        }
    },

    async refreshMessagesSilent() {
        return this.loadMessages(false);
    },

    /** «Ver mensajes anteriores»: the page before the oldest shown, keeping the reader's place. */
    async loadOlder() {
        const channel = this._selectedCanalId, epoch = this._viewEpoch, oldest = this._mensajes[0]?.cursor;
        if (this._loadingOlder || !this._hasOlder || !Number.isSafeInteger(oldest)) return;
        this._loadingOlder = true;
        this.renderMessagesList();
        try {
            const res = await fetch(`/api/comunidad/canales/${encodeURIComponent(channel)}/mensajes?before=${oldest}`, { headers: { 'X-SICAF-CSRF': AuthManager.getCsrfToken() } });
            const data = await res.json();
            if (!this._active || epoch !== this._viewEpoch || channel !== this._selectedCanalId || this._mensajes[0]?.cursor !== oldest) return;
            if (!res.ok || !data.success) throw new Error();
            const pane = document.getElementById('comunidad-chat-messages');
            const height = pane?.scrollHeight || 0;
            this._mensajes = [...(data.mensajes || []), ...this._mensajes];
            this._hasOlder = !!data.hasMore;
            this._loadingOlder = false;
            this.renderMessagesList();
            if (pane) pane.scrollTop += pane.scrollHeight - height;
        } catch {
            Utils.showToast('No se pudieron cargar los mensajes anteriores.', 'error');
        } finally {
            if (this._loadingOlder) { this._loadingOlder = false; this.renderMessagesList(); }
        }
    },

    renderMessagesList() {
        const container = document.getElementById('comunidad-chat-messages');
        if (!container) return;

        if (this._loadError) {
            SafeHTML.set(container, `<div role="alert"><p>${Utils.sanitizeHTML(this._loadError)}</p><button class="btn btn-secondary" ${UIEvents.attr("click", function(event,captured){ComunidadView.loadCanales();}, [])}>Reintentar</button></div>`);
            return;
        }

        if (this._mensajes.length === 0) {
            SafeHTML.set(container, `
                <div class="cmx-empty">
                    <i class="fas fa-comments" aria-hidden="true"></i>
                    <b>No hay mensajes en este canal</b>
                    <span>¡Sé el primero en iniciar la conversación!</span>
                </div>`);
            return;
        }

        const currentUser = AuthManager.getCurrentUser();
        const isAdmin = currentUser && currentUser.role === 'admin';
        const myId = currentUser ? currentUser.id : null;
        const boundary = this._unreadBoundary;
        const firstUnread = boundary && (this._mensajes.find(m => m.id === boundary.id) ||
            this._mensajes.find(m => Number.isSafeInteger(boundary.sequence) && m.sequence >= boundary.sequence));

        const older = this._hasOlder ? `
            <button type="button" class="cmx-older" ${UIEvents.attr("click", function(event,captured){ComunidadView.loadOlder();}, [])} ${this._loadingOlder ? 'disabled' : ''}>
                <i class="fas fa-angle-up" aria-hidden="true"></i> ${this._loadingOlder ? 'Cargando…' : 'Ver mensajes anteriores'}
            </button>` : '';
        const roles = { student: 'Alumno', professor: 'Profesor', admin: 'Dirección', coordinator: 'Coordinación', rrhh: 'RRHH', warehouse: 'Almacén' };

        SafeHTML.set(container, older + this._mensajes.map(m => {
            const isMe = String(m.senderId) === String(myId);
            const time = new Date(m.createdAt).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
            const role = roles[m.senderRole] || 'Coordinación';
            const deleteBtn = (isAdmin || isMe) ? `<button type="button" class="cmx-msg-delete" aria-label="Eliminar mensaje" title="Eliminar mensaje"
                ${UIEvents.attr("click", function(event,captured){ComunidadView.deleteMessage(String(captured[0]));}, [m.id])}><i class="fas fa-trash-alt" aria-hidden="true"></i></button>` : '';
            return `
            ${firstUnread && m.id === firstUnread.id ? `<div id="comunidad-unread-divider" class="cmx-divider" role="separator" aria-label="Mensajes nuevos">
                <span aria-hidden="true"></span><b>${boundary.count} ${boundary.count === 1 ? 'mensaje nuevo' : 'mensajes nuevos'}</b><span aria-hidden="true"></span>
            </div>` : ''}
            <div class="cmx-msg ${isMe ? 'is-me' : ''}">
                ${isMe ? '' : `<span class="cmx-avatar" aria-hidden="true">${Utils.sanitizeHTML(String(m.senderName || '?').trim().charAt(0).toUpperCase())}</span>`}
                <div class="cmx-msg-body">
                    <div class="cmx-msg-meta">${isMe ? '<b>Tú</b>' : `<b>${Utils.sanitizeHTML(m.senderName)}</b> <span>${role}</span>`} <span>· ${time}</span>${deleteBtn}</div>
                    <div class="cmx-bubble">${Utils.sanitizeHTML(m.text)}</div>
                </div>
            </div>`;
        }).join(''));
    },

    scrollToBottom() {
        const container = document.getElementById('comunidad-chat-messages');
        if (container) {
            container.scrollTop = container.scrollHeight;
        }
    },

    scrollToUnread() {
        const container = document.getElementById('comunidad-chat-messages');
        const divider = document.getElementById('comunidad-unread-divider');
        if (container && divider) {
            container.scrollTop += divider.getBoundingClientRect().top - container.getBoundingClientRect().top - 16;
        }
    },

    // Intro sends; Mayús+Intro (or Alt) makes a new line in the message.
    handleInputKeydown(e) {
        if (e.key === 'Enter' && !e.shiftKey && !e.altKey && !e.isComposing) {
            e.preventDefault();
            this.sendMessage();
        }
    },

    async sendMessage() {
        const input = document.getElementById('comunidad-msg-input');
        if (!input || this._sending || this._chatBlocked || !this._selectedCanalId || this._loadError) return;
        const text = input.value.trim();
        if (!text) return;

        const channel = this._selectedCanalId, epoch = this._viewEpoch;
        const token = AuthManager.getCsrfToken();
        this._sending = true;
        this.updateInputState();
        try {
            const res = await fetch(`/api/comunidad/canales/${encodeURIComponent(channel)}/mensajes`, {
                method: 'POST',
                headers: {
                    'X-SICAF-CSRF': token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ text })
            });

            const data = await res.json();
            if (!this._active || epoch !== this._viewEpoch || token !== AuthManager.getCsrfToken()) return;
            if (res.ok && data.success) {
                if (channel === this._selectedCanalId && input.value.trim() === text) input.value = '';
                // Instantly load without waiting poll
                if (channel === this._selectedCanalId) await this.loadMessages(true);
            } else {
                if (typeof Utils !== 'undefined' && Utils.showToast) {
                    Utils.showToast(data.error || 'No se pudo enviar el mensaje', 'error');
                } else if (typeof Swal !== 'undefined') {
                    SafeHTML.fire('Error', data.error || 'No se pudo enviar el mensaje', 'error');
                } else {
                    alert(data.error || 'No se pudo enviar el mensaje');
                }
            }
        } catch(e) {
            console.error('Error al enviar mensaje:', e);
            if (typeof Utils !== 'undefined' && Utils.showToast) {
                Utils.showToast('Error al enviar el mensaje', 'error');
            }
        } finally {
            this._sending = false;
            this.updateInputState();
        }
    },

    async deleteMessage(msgId) {
        const confirmResult = await SafeHTML.fire({
            title: '¿Eliminar mensaje?',
            text: 'Esta acción no se puede deshacer y el mensaje se borrará para todos.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#64748b'
        });

        if (!confirmResult.isConfirmed) return;

        try {
            const token = AuthManager.getCsrfToken();
            const res = await fetch(`/api/comunidad/mensajes/${msgId}`, {
                method: 'DELETE',
                headers: {
                    'X-SICAF-CSRF': token
                }
            });

            const data = await res.json();
            if (data.success) {
                if (typeof Utils !== 'undefined' && Utils.showToast) {
                    Utils.showToast('Mensaje eliminado', 'success');
                }
                await this.loadMessages(false);
            } else {
                if (typeof Utils !== 'undefined' && Utils.showToast) {
                    Utils.showToast(data.error || 'No se pudo eliminar el mensaje', 'error');
                }
            }
        } catch(e) {
            console.error('Error deleting message:', e);
            if (typeof Utils !== 'undefined' && Utils.showToast) {
                Utils.showToast('Error al eliminar el mensaje', 'error');
            }
        }
    },

    openCreateChannelModal() {
        // Cargar formaciones para asociar target
        if (this._chatBlocked || !this._active) return;
        const formations = ((typeof DataManager !== 'undefined' && DataManager.getFormations) ? DataManager.getFormations() : [])
            .filter(f => this._allowedTargetFormations === null || (this._allowedTargetFormations || []).includes(f.id));

        const targetSelectHtml = `
        <div style="margin-top:1rem; text-align:left;">
            <label style="font-size:0.8rem; font-weight:700; display:block; margin-bottom:8px; color:#475569;">Cursos con acceso (sin selección: toda la escuela; el personal conserva acceso)</label>
            <div style="display:grid; grid-template-columns:repeat(auto-fill, minmax(180px, 1fr)); gap:0.5rem; max-height:160px; overflow-y:auto; padding:0.5rem; border:1px solid #cbd5e1; border-radius:8px; background:#f8fafc;">
                ${formations.map(f => `
                    <label style="display:flex; align-items:center; gap:8px; font-size:0.82rem; cursor:pointer; font-weight:600; color:#334155; user-select:none;">
                        <input type="checkbox" class="new-channel-target-checkbox" value="${Utils.sanitizeHTML(f.id)}" style="width:16px; height:16px; cursor:pointer;">
                        <span>${Utils.sanitizeHTML(f.name)}</span>
                    </label>
                `).join('')}
            </div>
        </div>`;

        const user = AuthManager.getCurrentUser();
        const isAdmin = user && user.role === 'admin';

        const defaultChannelHtml = isAdmin ? `
        <div style="margin-top:1rem; text-align:left;">
            <label style="display:flex; align-items:center; gap:8px; font-size:0.85rem; cursor:pointer; font-weight:700; color:#334155; user-select:none;">
                <input type="checkbox" id="new-channel-isdefault" style="width:16px; height:16px; cursor:pointer;">
                <span>Hacer canal General 🎬 (Acceso para toda la escuela)</span>
            </label>
        </div>
        ` : '';

        SafeHTML.fire({
            title: '<span style="font-size:1.1rem; font-weight:800; display:inline-flex; align-items:center; gap:8px;"><i class="fas fa-comments" style="color:#2563eb;"></i>Crear Nuevo Canal / Casting</span>',
            html: `
            <div style="text-align:left; font-family:inherit;">
                <div>
                    <label style="font-size:0.8rem; font-weight:700; display:block; margin-bottom:6px; color:#475569;">Nombre del Canal / Proyecto</label>
                    <input type="text" id="new-channel-name" placeholder="Ej: Casting cortometraje de ficción" style="width:100%; border:1px solid #cbd5e1; border-radius:8px; padding:8px 12px; font-size:0.88rem; font-family:inherit; box-sizing:border-box;">
                </div>
                <div style="margin-top:1rem;">
                    <label style="font-size:0.8rem; font-weight:700; display:block; margin-bottom:6px; color:#475569;">Descripción / Objetivos</label>
                    <textarea id="new-channel-desc" rows="3" placeholder="Describe brevemente de qué trata este canal colaborativo..." style="width:100%; border:1px solid #cbd5e1; border-radius:8px; padding:8px 12px; font-size:0.88rem; font-family:inherit; box-sizing:border-box; resize:vertical;"></textarea>
                </div>
                ${targetSelectHtml}
                ${defaultChannelHtml}
            </div>`,
            showCancelButton: true,
            confirmButtonText: 'Crear Canal',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#2563eb',
            preConfirm: () => {
                const name = document.getElementById('new-channel-name').value.trim();
                const description = document.getElementById('new-channel-desc').value.trim();
                const isDefault = document.getElementById('new-channel-isdefault')?.checked || false;
                
                // Recoger IDs de formaciones marcadas
                const checkedBoxes = document.querySelectorAll('.new-channel-target-checkbox:checked');
                const targetFormations = Array.from(checkedBoxes).map(cb => cb.value);

                if (!name) {
                    Swal.showValidationMessage('El nombre del canal es obligatorio');
                    return false;
                }
                if (name.length > 120 || description.length > 2000) {
                    Swal.showValidationMessage('El nombre admite 120 caracteres y la descripción 2000.');
                    return false;
                }
                if (isDefault && targetFormations.length) {
                    Swal.showValidationMessage('Un canal general es para toda la escuela. Desmarca los cursos o la opción de canal general.');
                    return false;
                }
                return { name, description, targetFormations, isDefault };
            }
        }).then(async result => {
            if (!result.isConfirmed) return;
            try {
                const token = AuthManager.getCsrfToken();
                const res = await fetch('/api/comunidad/canales', {
                    method: 'POST',
                    headers: {
                        'X-SICAF-CSRF': token,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(result.value)
                });
                const data = await res.json();
                if (!data.success) throw new Error(data.error);

                SafeHTML.fire({ icon: data.notificationsSent === false ? 'warning' : 'success', title: 'Canal creado', text: data.notificationsSent === false ? 'El canal está guardado, pero no se pudieron enviar los avisos. No es necesario crearlo otra vez.' : 'El canal se ha habilitado y los destinatarios han sido notificados.', showConfirmButton: true });
                
                await this.loadCanales();
                if (data.canal && data.canal.id) {
                    this.openChannel(data.canal.id);
                }

                // Sincronizar notificaciones inmediatamente para pintar el puntito rojo al vuelo
                if (typeof FileStorage !== 'undefined' && FileStorage.syncNotifications) {
                    await FileStorage.syncNotifications();
                }
            } catch(e) {
                SafeHTML.fire('Error', e.message, 'error');
            }
        });
    },

    deleteChannel(event, canalId) {
        if (event) event.stopPropagation(); // Evitar cambiar de canal al pulsar borrar
        
        const canal = this._canales.find(c => c.id === canalId);
        if (!canal) return;

        SafeHTML.fire({
            title: '¿Eliminar canal?',
            text: `¿Estás seguro de que quieres eliminar el canal "#${canal.name}" y todos sus mensajes de forma permanente? Esta acción no se puede deshacer.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#64748b'
        }).then(async result => {
            if (!result.isConfirmed) return;
            try {
                const token = AuthManager.getCsrfToken();
                const res = await fetch(`/api/comunidad/canales/${canalId}`, {
                    method: 'DELETE',
                    headers: { 'X-SICAF-CSRF': token }
                });
                const data = await res.json();
                if (!data.success) throw new Error(data.error);

                SafeHTML.fire({ icon: 'success', title: 'Canal eliminado', timer: 1500, showConfirmButton: false });
                
                // Si eliminamos el canal activo, redirigimos al general
                if (this._selectedCanalId === canalId) {
                    this._selectedCanalId = 'canal_general';
                }
                
                await this.loadCanales();
            } catch(e) {
                SafeHTML.fire('Error', e.message, 'error');
            }
        });
    }
};

window.ComunidadView = ComunidadView;
